import {
    QueryClient,
    useMutation,
    UseMutationResult,
    useQuery,
    useQueryClient,
    UseQueryResult
} from '@tanstack/react-query';
import { RuleModel } from 'src/types/rule.model';
import RuleService from 'src/services/rule.service';
import { IncomeSourceRuleModel } from 'src/types/income-source-rule.model';
import IncomeSourceRuleService from 'src/services/income-source-rule.service';
import { invalidateTransactionsSpending } from './spending';

const rulesQueryKey = 'expenseRules';

export const useRules = (): UseQueryResult<RuleModel[]> => {
    return useQuery<RuleModel[]>({
        queryKey: [rulesQueryKey],
        queryFn: async () => await RuleService.getRules()
    });
};

export const useUpdateRule = (): UseMutationResult<void, unknown, Partial<RuleModel>> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (rule: Partial<RuleModel>) => await RuleService.createOrUpdateRule(rule),
        onSuccess: (): void => {
            void invalidateRules(queryClient);
            void invalidateTransactionsSpending(queryClient);
        }
    });
};

export const useCreateRules = (): UseMutationResult<RuleModel, unknown, Array<Partial<RuleModel>>> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (rules: Array<Partial<RuleModel>>) => await RuleService.createRules(rules),
        onSuccess: (): void => {
            void invalidateRules(queryClient);
            void invalidateTransactionsSpending(queryClient);
        }
    });
};

export const useDeleteRule = (): UseMutationResult<number, unknown, string> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (ruleId: string) => await RuleService.deleteRule(ruleId),
        onSuccess: (): void => {
            void invalidateRules(queryClient);
            void invalidateTransactionsSpending(queryClient);
        }
    });
};

export const useUpdateIncomeRule = (): UseMutationResult<void, unknown, Partial<IncomeSourceRuleModel>> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (rule: Partial<IncomeSourceRuleModel>) =>
            await IncomeSourceRuleService.createOrUpdateRule(rule),
        onSuccess: (): void => {
            void invalidateTransactionsSpending(queryClient);
        }
    });
};

export const useCreateIncomeRules = (): UseMutationResult<
    IncomeSourceRuleModel,
    unknown,
    Array<Partial<IncomeSourceRuleModel>>
> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (rules: Array<Partial<IncomeSourceRuleModel>>) =>
            await IncomeSourceRuleService.createRules(rules),
        onSuccess: (): void => {
            void invalidateTransactionsSpending(queryClient);
        }
    });
};

export const useDeleteIncomeRule = (): UseMutationResult<number, unknown, Partial<IncomeSourceRuleModel>> => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (rule: Partial<IncomeSourceRuleModel>) => await IncomeSourceRuleService.deleteRule(rule),
        onSuccess: (): void => {
            void invalidateTransactionsSpending(queryClient);
        }
    });
};

const invalidateRules = async (queryClient: QueryClient): Promise<void> => {
    await queryClient.invalidateQueries([rulesQueryKey]);
};
